import React, {
    createContext,
    useContext,
    useState,
    useCallback,
    useEffect,
    useMemo,
    useRef,
} from "react";

/**
 * Globalny system powiadomień „toast" — port 1:1 z mPracownika.
 *
 * Użycie:
 *   const { showToast } = useToast();          // najczęstszy wariant
 *   const { success, error: showError } = useToast();
 *   const toast = useToast(); toast.success("Zapisano.");
 *
 * UWAGA: `duration` jest w SEKUNDACH (domyślnie 5), nie w milisekundach.
 */
const ToastContext = createContext(null);

export const ToastProvider = ({ children }) => {
    const [toasts, setToasts] = useState([]);

    const showToast = useCallback((message, type = "info", duration = 5) => {
        const id = Date.now() + Math.random();
        setToasts((prevToasts) => [...prevToasts, { id, message, type, duration }]);
    }, []);

    const removeToast = useCallback((id) => {
        setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== id));
    }, []);

    const success = useCallback(
        (message, duration) => {
            showToast(message, "success", duration);
        },
        [showToast]
    );

    const error = useCallback(
        (message, duration) => {
            showToast(message, "error", duration);
        },
        [showToast]
    );

    const info = useCallback(
        (message, duration) => {
            showToast(message, "info", duration);
        },
        [showToast]
    );

    const warning = useCallback(
        (message, duration) => {
            showToast(message, "warning", duration);
        },
        [showToast]
    );

    // Nasłuch konfliktu współbieżności (409) z interceptora apiService.
    // Emisja po stronie HTTP:
    //   window.dispatchEvent(new CustomEvent("concurrency-conflict", { detail: { message } }));
    useEffect(() => {
        const handleConflict = (e) => {
            const { message } = e.detail || {};
            showToast(message || "Dane zostały zmienione przez innego użytkownika.", "warning", 10);
        };
        window.addEventListener("concurrency-conflict", handleConflict);
        return () => window.removeEventListener("concurrency-conflict", handleConflict);
    }, [showToast]);

    // Stała tożsamość obiektu kontekstu — bez tego każdy toast unieważniał
    // useCallback-i konsumentów zależne od toast (ryzyko pętli ładowania).
    const contextValue = useMemo(
        () => ({ showToast, success, error, info, warning }),
        [showToast, success, error, info, warning]
    );

    return (
        <ToastContext.Provider value={contextValue}>
            {children}
            <ToastContainer toasts={toasts} removeToast={removeToast} />
        </ToastContext.Provider>
    );
};

export const useToast = () => {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error("useToast must be used within a ToastProvider");
    }
    return context;
};

const ToastContainer = ({ toasts, removeToast }) => {
    return (
        <div className="toast-container" role="region" aria-label="Powiadomienia">
            {toasts.map((toast) => (
                <ToastMessage
                    key={toast.id}
                    id={toast.id}
                    message={toast.message}
                    type={toast.type}
                    onClose={removeToast}
                    duration={toast.duration}
                />
            ))}
        </div>
    );
};

const ToastMessage = ({ id, message, type, onClose, duration }) => {
    const DURATION = (duration ?? 5) * 1000;
    const [isPaused, setIsPaused] = useState(false);
    const [isDisappearing, setIsDisappearing] = useState(false);
    const timerRef = useRef(null);
    const remainingTimeRef = useRef(DURATION);
    const startTimeRef = useRef(null);

    const internalClose = useCallback(() => {
        if (isDisappearing) return;

        setIsPaused(true);
        clearTimeout(timerRef.current);
        setIsDisappearing(true);
    }, [isDisappearing]);

    const pause = useCallback(() => {
        if (isPaused || isDisappearing) return;
        clearTimeout(timerRef.current);
        const timeElapsed = Date.now() - startTimeRef.current;
        remainingTimeRef.current -= timeElapsed;
        setIsPaused(true);
    }, [isPaused, isDisappearing]);

    const resume = useCallback(() => {
        if (!isPaused || isDisappearing) return;
        startTimeRef.current = Date.now();
        timerRef.current = setTimeout(internalClose, remainingTimeRef.current);
        setIsPaused(false);
    }, [isPaused, isDisappearing, internalClose]);

    useEffect(() => {
        startTimeRef.current = Date.now();
        timerRef.current = setTimeout(internalClose, DURATION);
        return () => clearTimeout(timerRef.current);
    }, [internalClose, DURATION]);

    const handleAnimationEnd = (e) => {
        if (e.animationName === "toast-slide-out") {
            onClose(id);
        }
    };

    const typeClass = `toast-${type}`;

    return (
        <div
            className={`toast ${typeClass} ${isDisappearing ? "is-disappearing" : ""}`}
            role={type === "error" ? "alert" : "status"}
            aria-live={type === "error" ? "assertive" : "polite"}
            onMouseEnter={pause}
            onMouseLeave={resume}
            onAnimationEnd={handleAnimationEnd}
        >
            <div className="toast-icon"></div>
            <div className="toast-content">
                {/* Zabezpieczenie: gdy ktoś poda obiekt (np. ProblemDetails z API) React wywaliłby całą stronę
                    ("Objects are not valid as a React child" → biały ekran). Zawsze renderujemy tekst. */}
                <p>
                    {typeof message === "string"
                        ? message
                        : message?.message || message?.title || message?.detail || "Wystąpił błąd."}
                </p>
                <div className="toast-timer-bar-wrapper">
                    <div
                        className="toast-timer-bar"
                        style={{
                            animationDuration: `${DURATION}ms`,
                            animationPlayState: isPaused ? "paused" : "running",
                        }}
                    ></div>
                </div>
            </div>
            <button onClick={internalClose} className="toast-close-btn" aria-label="Zamknij powiadomienie">
                &times;
            </button>
        </div>
    );
};
